import React from 'react';
import { DocumentTextIcon, StarIcon, UserIcon } from '../../../components/ui/Icons';
import { cn } from '../../../lib/utils';

export type QuickFilterKey =
  | 'unread'
  | 'starred'
  | 'assignedToMe'
  | 'statusOpen'
  | 'statusWorking'
  | 'statusClosed'
  | 'attachments'
  | 'linked'
  | 'archived';

export type SearchInFieldKey = 'sender' | 'recipients' | 'subject' | 'body' | 'attachments';

function StatusDot({ colorClass, active }: { colorClass: string; active?: boolean }) {
  return (
    <span
      className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', active ? 'bg-white' : colorClass)}
      aria-hidden
    />
  );
}

function PaperclipIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
      />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );
}

function ArchiveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
      />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function SubjectIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h6m-6 4h10" />
    </svg>
  );
}

function iconTone(active: boolean | undefined, inactive: string): string {
  return cn('w-4 h-4 flex-shrink-0', active ? 'text-white' : inactive);
}

export function QuickFilterIcon({
  filterKey,
  active,
  compact,
}: {
  filterKey: QuickFilterKey;
  active?: boolean;
  compact?: boolean;
}) {
  const size = compact ? 'w-3 h-3' : 'w-4 h-4';

  switch (filterKey) {
    case 'unread':
      return (
        <StatusDot
          colorClass="bg-blue-500"
          active={active}
        />
      );
    case 'starred':
      return (
        <StarIcon
          filled
          className={cn(size, active ? 'text-white' : 'text-amber-500')}
        />
      );
    case 'assignedToMe':
      return <UserIcon className={iconTone(active, 'text-indigo-500')} />;
    case 'statusOpen':
      return <StatusDot colorClass="bg-emerald-500" active={active} />;
    case 'statusWorking':
      return <StatusDot colorClass="bg-amber-500" active={active} />;
    case 'statusClosed':
      return <StatusDot colorClass="bg-slate-400 dark:bg-slate-500" active={active} />;
    case 'attachments':
      return <PaperclipIcon className={iconTone(active, 'text-gray-500')} />;
    case 'linked':
      return <LinkIcon className={iconTone(active, 'text-purple-500')} />;
    case 'archived':
      return <ArchiveIcon className={iconTone(active, 'text-amber-700 dark:text-amber-500')} />;
    default:
      return null;
  }
}

export function SearchInFieldIcon({
  fieldKey,
  active,
}: {
  fieldKey: SearchInFieldKey;
  active?: boolean;
}) {
  switch (fieldKey) {
    case 'sender':
      return <UserIcon className={iconTone(active, 'text-indigo-500')} />;
    case 'recipients':
      return <UsersIcon className={iconTone(active, 'text-sky-500')} />;
    case 'subject':
      return <SubjectIcon className={iconTone(active, 'text-violet-500')} />;
    case 'body':
      return <DocumentTextIcon className={iconTone(active, 'text-gray-500')} />;
    case 'attachments':
      return <PaperclipIcon className={iconTone(active, 'text-gray-500')} />;
    default:
      return null;
  }
}
